import { IAttribute, ISearchRequest } from '../../models/Search';

/**
 * Shorthand product-search syntax for the global search palette.
 *
 * A query starting with ">" is a pure passthrough product search; a query containing any
 * recognized key:value token implies one alongside the normal sections. Tokens:
 *
 *   seg:X segment:X          → criteria.segment
 *   cat:X category:X         → criteria.category
 *   sub:X subcategory:X      → criteria.subCategory
 *   mf:X mfr:X mfg:X manufacturer:X brand:X → criteria.manufacturer
 *   mpn:X mfp:X part:X       → criteria.manufacturerPartNumber (exact phrase)
 *   vendor:X                 → criteria.vendor
 *   storefront:X             → storefrontNames (resolved to storefrontFilter ids at dispatch,
 *                              implies inStorefront)
 *   name=value               → boost ("nice to have") attribute {type: 'facet', name, value};
 *                              quote spaced parts: "Memory Size"="32 GB"
 *   name==value              → must (required) attribute — hard-filters instead of ranking
 *   instock                  → criteria.inStock (the passthrough DEFAULTS to in-stock true;
 *                              use anystock to include out-of-stock products)
 *   instorefront             → criteria.inStorefront (appears on a storefront)
 *   price:100-500            → criteria.lowPrice/highPrice (also price:<500, price:>100,
 *                              price:-500, price:100-; a bare price:500 means "up to 500")
 *
 * Values keep the casing the user typed (keyword-field filters downstream are case-sensitive),
 * quotes group multi-word values (cat:"Gaming Laptops"), and everything else is keywords.
 */

export interface IParsedProductQuery {
    /** True when the query is/implies a product search (sigil or at least one sugar token). */
    active: boolean;
    /** True when the query started with ">" — the palette shows only the passthrough row. */
    sigil: boolean;
    /** Ready to spread over the default criteria and dispatch. */
    criteria: Partial<ISearchRequest>;
    keywords: string;
    /** Human-readable chips for the row's secondary line ("Category: Laptops", "RAM = 16GB"). */
    summary: string[];
    /** Verbatim tokens recognized as sugar (key:value, name=value, instock) — for query rewriting. */
    sugarTokens: string[];
    /** Verbatim bare keyword tokens — for query rewriting (suggestion pills consume these). */
    keywordTokens: string[];
    /** storefront:X values by name — the dispatcher resolves them to ids for storefrontFilter. */
    storefrontNames: string[];
    /** A trailing `name=` with no value yet — an attribute the user is mid-way through entering. */
    pendingAttribute?: string;
}

const KEY_MAP: { [key: string]: 'segment' | 'category' | 'subCategory' | 'manufacturer' | 'manufacturerPartNumber' | 'vendor' | 'storefront' } = {
    seg: 'segment',
    segment: 'segment',
    cat: 'category',
    category: 'category',
    sub: 'subCategory',
    subcategory: 'subCategory',
    mf: 'manufacturer',
    mfr: 'manufacturer',
    mfg: 'manufacturer',
    manufacturer: 'manufacturer',
    brand: 'manufacturer',
    mpn: 'manufacturerPartNumber',
    mfp: 'manufacturerPartNumber',
    part: 'manufacturerPartNumber',
    vendor: 'vendor',
    storefront: 'storefront',
    store: 'storefront',
};

const FIELD_LABELS: { [key: string]: string } = {
    segment: 'Segment',
    category: 'Category',
    subCategory: 'Subcategory',
    manufacturer: 'Manufacturer',
    manufacturerPartNumber: 'MPN',
    vendor: 'Vendor',
    storefront: 'Storefront',
};

/** "100-500" → both bounds; "<500"/"-500" → high only; ">100"/"100-" → low only; "500" → high. */
const parsePriceRange = (value: string): { low?: number, high?: number } | undefined => {
    const clean = value.replace(/[$,]/g, '');
    const num = (v?: string) => {
        const n = parseFloat(v ?? '');
        return isNaN(n) ? undefined : n;
    };
    let m = clean.match(/^([\d.]+)?-([\d.]+)?$/);
    if (m && (m[1] || m[2])) return { low: num(m[1]), high: num(m[2]) };
    m = clean.match(/^<([\d.]+)$/);
    if (m) return { high: num(m[1]) };
    m = clean.match(/^>([\d.]+)$/);
    if (m) return { low: num(m[1]) };
    m = clean.match(/^([\d.]+)$/);
    if (m) return { high: num(m[1]) };
    return undefined;
};

/**
 * Title-cases each word ("hewlett packard" → "Hewlett Packard") while leaving interior casing
 * alone ("HP" stays "HP", "IngramMicro" stays "IngramMicro"). Taxonomy fields filter against
 * case-sensitive keyword values downstream, and catalog values are conventionally Title Case.
 */
const titleCase = (value: string): string =>
    value.split(' ').map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w)).join(' ');

const unquote = (value: string): string =>
    value.length >= 2 && value.startsWith('"') && value.endsWith('"') ? value.slice(1, -1) : value;

export const parseProductQuery = (rawTerm: string): IParsedProductQuery => {
    const sigil = rawTerm.startsWith('>');
    const body = (sigil ? rawTerm.slice(1) : rawTerm).trim();

    // Tokens are space-separated, but quoted runs stay together: cat:"Gaming Laptops" is one token.
    const tokens = body.match(/(?:[^\s"]+"[^"]*"|"[^"]*"|[^\s"]+)+/g) ?? [];

    const keywords: string[] = [];
    const sugarTokens: string[] = [];
    const summary: string[] = [];
    const manufacturer: string[] = [];
    const vendor: string[] = [];
    const storefrontNames: string[] = [];
    const boost: IAttribute[] = [];
    const must: IAttribute[] = [];
    const criteria: Partial<ISearchRequest> = {};
    let hasSugar = false;
    let pendingAttribute: string | undefined;

    for (const token of tokens) {
        const priced = token.match(/^price:(.+)$/i);
        if (priced) {
            const range = parsePriceRange(unquote(priced[1]));
            if (range) {
                hasSugar = true;
                sugarTokens.push(token);
                if (range.low !== undefined) criteria.lowPrice = range.low;
                if (range.high !== undefined) criteria.highPrice = range.high;
                summary.push(
                    range.low !== undefined && range.high !== undefined ? `Price: $${range.low} to $${range.high}`
                        : range.low !== undefined ? `Price: from $${range.low}`
                        : `Price: up to $${range.high}`
                );
                continue;
            }
        }

        const keyed = token.match(/^([a-zA-Z]+):(.+)$/);
        const mappedField = keyed ? KEY_MAP[keyed[1].toLowerCase()] : undefined;
        if (keyed && mappedField) {
            hasSugar = true;
            sugarTokens.push(token);
            // Part numbers stay as typed (matched case-insensitively); taxonomy values normalize.
            const value = mappedField === 'manufacturerPartNumber'
                ? unquote(keyed[2])
                : titleCase(unquote(keyed[2]));
            if (mappedField === 'manufacturer') manufacturer.push(value);
            else if (mappedField === 'vendor') vendor.push(value);
            else if (mappedField === 'storefront') {
                storefrontNames.push(value);
                criteria.inStorefront = true;
            }
            else criteria[mappedField] = value;
            summary.push(`${FIELD_LABELS[mappedField]}: ${value}`);
            continue;
        }

        // A dangling `name=` is an attribute mid-entry (usually from tab-completing the name):
        // keep the token as sugar so query rebuilds preserve it, and surface the name so the
        // palette can offer that attribute's values.
        const pending = token.match(/^("[^"]*"|[^\s="]+)=$/);
        if (pending) {
            hasSugar = true;
            sugarTokens.push(token);
            pendingAttribute = unquote(pending[1]);
            continue;
        }

        // A double equals is a MUST: the attribute hard-filters results instead of ranking
        // them. This exists mostly so criteria round-trip through chips without demoting a
        // sidebar-picked must filter to a boost.
        const mustAttr = token.match(/^("[^"]*"|[^\s="]+)==("[^"]*"|.+)$/);
        if (mustAttr) {
            hasSugar = true;
            sugarTokens.push(token);
            const name = unquote(mustAttr[1]);
            const value = unquote(mustAttr[2]);
            must.push({ type: 'facet', name, value });
            summary.push(`${name} = ${value} (required)`);
            continue;
        }

        // Name and value are each either a quoted phrase or a run without spaces/equals, so
        // "Memory Size"="32 GB" survives tokenization and lands here as one token.
        const attr = token.match(/^("[^"]*"|[^\s="]+)=("[^"]*"|.+)$/);
        if (attr) {
            hasSugar = true;
            sugarTokens.push(token);
            const name = unquote(attr[1]);
            const value = unquote(attr[2]);
            boost.push({ type: 'facet', name, value });
            summary.push(`${name} = ${value}`);
            continue;
        }

        if (token.toLowerCase() === 'instock' || token.toLowerCase() === 'in-stock') {
            hasSugar = true;
            sugarTokens.push(token);
            criteria.inStock = true;
            summary.push('In stock');
            continue;
        }

        if (['anystock', 'any-stock'].includes(token.toLowerCase())) {
            hasSugar = true;
            sugarTokens.push(token);
            criteria.inStock = false;
            summary.push('Include out of stock');
            continue;
        }

        if (['instorefront', 'in-storefront', 'onstorefront', 'on-storefront'].includes(token.toLowerCase())) {
            hasSugar = true;
            sugarTokens.push(token);
            criteria.inStorefront = true;
            summary.push('On storefront');
            continue;
        }

        keywords.push(unquote(token));
    }

    if (manufacturer.length) criteria.manufacturer = manufacturer;
    if (vendor.length) criteria.vendor = vendor;
    // Attributes ride as boosts ("nice to have"): they rank matching products up without
    // filtering out an otherwise-good result that lacks the attribute value.
    if (boost.length) criteria.boost = boost;
    if (must.length) criteria.must = must;
    const keywordString = keywords.join(' ');
    if (keywordString) criteria.keywords = keywordString;

    return {
        active: sigil || hasSugar,
        sigil,
        criteria,
        keywords: keywordString,
        summary,
        sugarTokens,
        keywordTokens: keywords,
        storefrontNames,
        pendingAttribute,
    };
};

/** The verbatim tokens of a passthrough query body, quotes kept together — for rebuilds. */
export const tokenizeProductQuery = (rawTerm: string): string[] => {
    const body = (rawTerm.startsWith('>') ? rawTerm.slice(1) : rawTerm).trim();
    return body.match(/(?:[^\s"]+"[^"]*"|"[^"]*"|[^\s"]+)+/g) ?? [];
};

/**
 * The VERBATIM keyword portion of a free-text query: sugar tokens drop out, but keyword tokens
 * keep their original quoting ("ABC123" stays quoted). parseProductQuery unquotes keywords for
 * matching, which is right for the palette's transient dispatch but wrong for the search page,
 * where quoted keywords mean exact-phrase matching downstream.
 */
export const extractKeywordText = (text: string): string =>
    tokenizeProductQuery(text)
        .filter((t) => parseProductQuery(t).sugarTokens.length === 0)
        .join(' ');

/** A token that parses to exactly one complete sugar token (no pending `name=`). */
export const isCompleteSugarToken = (t: string): boolean => {
    const p = parseProductQuery(t);
    return p.sugarTokens.length === 1 && !p.pendingAttribute;
};

/** Attribute sugar in any form: name=value, name==value, or a pending name=. */
export const isAttributeSugarToken = (t: string): boolean => /^("[^"]*"|[^\s="]+)=/.test(t);

/** Human chip label for one sugar token — its own one-token parse summary. */
export const sugarChipLabel = (token: string): string => parseProductQuery(token).summary[0] ?? token;

/**
 * Chip removal with the taxonomy cascade: cancelling a category takes its subcategory with it,
 * and when the last category / subcategory chip goes, the attribute filters it defined go too.
 *
 * The cascade only fires for a removed taxonomy chip. Attribute chips added on their own (a
 * bullet click, the facet panel, a typed name==value) have no category defining them, so
 * removing one of them, or a manufacturer or price chip, leaves the other attributes alone.
 */
export const cascadeRemoveSugarToken = (chips: string[], token: string): string[] => {
    let rest = chips.filter((t) => t !== token);
    const isTaxonomy = (t: string) => /^(cat|category|sub|subcategory):/i.test(t);
    if (!isTaxonomy(token)) return rest;
    if (/^(cat|category):/i.test(token)) {
        rest = rest.filter((t) => !/^(sub|subcategory):/i.test(t));
    }
    if (!rest.some(isTaxonomy)) {
        rest = rest.filter((t) => !isAttributeSugarToken(t));
    }
    return rest;
};

/** Quotes a pill/sugar value when it contains whitespace, so it survives re-parsing. */
export const quoteSugarValue = (value: string): string => (/[\s=]/.test(value) ? `"${value}"` : value);

/**
 * Reverse of parseProductQuery for the fields sugar can express: turns active criteria into
 * chips + free text so an inline product query box can hydrate from (and stay in sync with)
 * the search page's persisted criteria. Fields sugar cannot express (tags, range filters,
 * excludes, vendor stock scoping, …) are simply absent — the box must preserve them on apply.
 *
 * Storefront/in-stock state is deliberately NOT hydrated: the search sidebar owns those
 * controls, and a storefront id without its name loaded would round-trip into a dropped filter.
 * Grouped boosts (a pseudonym fan-out like "ram = 32gb") collapse back into the single chip
 * that created them; re-expansion happens at apply time against fresh facets.
 */
export const criteriaToSugarTokens = (criteria: Partial<ISearchRequest>): { chips: string[], keywords: string } => {
    const chips: string[] = [];
    for (const m of criteria.manufacturer ?? []) chips.push(`mfr:${quoteSugarValue(m)}`);
    if (criteria.manufacturerPartNumber) chips.push(`mpn:${quoteSugarValue(criteria.manufacturerPartNumber)}`);
    if (criteria.segment) chips.push(`seg:${quoteSugarValue(criteria.segment)}`);
    if (criteria.category) chips.push(`cat:${quoteSugarValue(criteria.category)}`);
    if (criteria.subCategory) chips.push(`sub:${quoteSugarValue(criteria.subCategory)}`);
    for (const v of criteria.vendor ?? []) chips.push(`vendor:${quoteSugarValue(v)}`);
    if (criteria.lowPrice !== undefined && criteria.highPrice !== undefined) chips.push(`price:${criteria.lowPrice}-${criteria.highPrice}`);
    else if (criteria.lowPrice !== undefined) chips.push(`price:>${criteria.lowPrice}`);
    else if (criteria.highPrice !== undefined) chips.push(`price:<${criteria.highPrice}`);
    const seenGroups = new Set<string>();
    for (const b of criteria.boost ?? []) {
        if (b.group) {
            if (seenGroups.has(b.group)) continue;
            seenGroups.add(b.group);
            // Groups are stamped as "name = value" by expandAttributeBoosts.
            const m = b.group.match(/^(.*?) = (.*)$/);
            if (m) chips.push(`${quoteSugarValue(m[1])}=${quoteSugarValue(m[2])}`);
            else chips.push(`${quoteSugarValue(b.name)}=${quoteSugarValue(b.value)}`);
        } else {
            chips.push(`${quoteSugarValue(b.name)}=${quoteSugarValue(b.value)}`);
        }
    }
    for (const m of criteria.must ?? []) chips.push(`${quoteSugarValue(m.name)}==${quoteSugarValue(m.value)}`);
    return { chips, keywords: criteria.keywords ?? '' };
};

/**
 * Pseudonym attributes: `ram=32gb` should reach the memory attributes a catalog actually has
 * without knowing their names. An alias maps to attribute-name fragments; values compare with
 * casing, spacing and punctuation stripped, so 32GB / 32gb / 32 GB / 32 gb are one value.
 */
export const ATTRIBUTE_ALIASES: { [alias: string]: string[] } = {
    ram: ['memory', 'ram'],
    memory: ['memory'],
    cpu: ['processor', 'cpu'],
    processor: ['processor'],
    gpu: ['graphics'],
    graphics: ['graphics'],
    storage: ['storage', 'drive', 'capacity'],
    disk: ['storage', 'drive', 'capacity'],
    ssd: ['ssd', 'solid state', 'storage'],
    hdd: ['hard drive', 'storage'],
    screen: ['display', 'screen'],
    display: ['display', 'screen'],
    resolution: ['resolution'],
    os: ['operating system', 'os'],
};

/**
 * Attribute-name fragments that DISQUALIFY a key from an alias's domain. Capacity pseudonyms
 * mean what is installed, not what the machine could hold - without this, "Maximum Supported
 * System Memory: 32 GB" boosts a 16 GB machine as hard as a real 32 GB one. Resolution has no
 * excludes: "Maximum Resolution" IS the standard name on monitors.
 */
const CAPACITY_EXCLUDES = ['maximum', 'max ', 'supported', 'expandable', 'upgradeable', 'upgradable'];
const CAPACITY_ALIASES = new Set(['ram', 'memory', 'storage', 'disk', 'ssd', 'hdd']);
const ATTRIBUTE_ALIAS_EXCLUDES: { [alias: string]: string[] } = {
    ram: CAPACITY_EXCLUDES,
    memory: CAPACITY_EXCLUDES,
    storage: CAPACITY_EXCLUDES,
    disk: CAPACITY_EXCLUDES,
    ssd: CAPACITY_EXCLUDES,
    hdd: CAPACITY_EXCLUDES,
};

/** A value that IS a capacity: "16 GB", "1.5 TB", "512GB SSD". Bare "5600" or "DDR5" is not. */
const CAPACITY_VALUE = /^\d+(?:\.\d+)?\s*(gb|tb|mb)\b/i;

/**
 * Whether a real attribute key belongs to a (pseudonym or literal) attribute name's domain.
 * Name fragments alone catch every memory-ADJACENT attribute ("Memory Speed": 5600, "Memory
 * Technology": DDR5), so for capacity pseudonyms the attribute's VALUES must also look like
 * capacities when they are available to check - a semantic test no name blocklist can match.
 */
export const aliasMatchesKey = (name: string, key: string, values?: string[]): boolean => {
    const lowerKey = key.toLowerCase();
    const lowerName = name.toLowerCase();
    const targets = ATTRIBUTE_ALIASES[lowerName] ?? [lowerName];
    // Word-boundary fragment match: "os" reaches the literal "OS" attribute without
    // false-matching keys that merely contain the letters ("Processor").
    const isWordChar = (c: string | undefined) => !!c && /[a-z0-9]/.test(c);
    const wordMatch = (t: string) => {
        let idx = lowerKey.indexOf(t);
        while (idx !== -1) {
            if (!isWordChar(lowerKey[idx - 1]) && !isWordChar(lowerKey[idx + t.length])) return true;
            idx = lowerKey.indexOf(t, idx + 1);
        }
        return false;
    };
    if (!targets.some(wordMatch)) return false;
    const excludes = ATTRIBUTE_ALIAS_EXCLUDES[lowerName] ?? [];
    if (excludes.some((x) => lowerKey.includes(x))) return false;
    if (CAPACITY_ALIASES.has(lowerName) && values && values.length > 0) {
        const capacityish = values.filter((v) => CAPACITY_VALUE.test(v.trim())).length;
        return capacityish >= Math.max(1, Math.ceil(values.length / 2));
    }
    return true;
};

// "windows" canonicalizes to "win" so win10 matches "Windows 10 Pro" and "Win10 Pro" alike.
/** Weight for a resolved attribute ask - above favorites (2.0) and has-image/cost (1.1). */
const ATTRIBUTE_ASK_WEIGHT = 3;

export const normalizeForMatch = (v: string): string =>
    v.toLowerCase().replace(/windows/g, 'win').replace(/[^a-z0-9]/g, '');

/**
 * Pseudonym attributes worth SUGGESTING as pills in the attribute builder. Each label fronts an
 * ATTRIBUTE_ALIASES entry; a pill only shows when its alias reaches at least one real attribute.
 */
export const PSEUDONYM_ATTRIBUTES: { label: string, alias: string }[] = [
    { label: 'RAM', alias: 'ram' },
    { label: 'Processor', alias: 'processor' },
    { label: 'Graphics', alias: 'gpu' },
    { label: 'Storage', alias: 'storage' },
    { label: 'Screen', alias: 'screen' },
    { label: 'Resolution', alias: 'resolution' },
    { label: 'OS', alias: 'os' },
];

/** "32gb" / "32 GB" / "1.5tb" → capacity in GB; undefined when no number is present. */
const parseCapacityGb = (v: string): number | undefined => {
    const m = v.match(/(\d+(?:\.\d+)?)\s*(tb|gb|mb)?/i);
    if (!m) return undefined;
    const n = parseFloat(m[1]);
    const unit = (m[2] ?? 'gb').toLowerCase();
    return unit === 'tb' ? n * 1024 : unit === 'mb' ? n / 1024 : n;
};

/**
 * Memory distributed across slot attributes (Griffin IT products carry "RAM Slot 1" / "RAM Slot 2"
 * instead of a total): boost slot values equal to the asked total or an equal split of it (32 in
 * one slot, or 16+16). The search facet aggregation returns only the TOP 5 values per attribute,
 * so the needed strings are often missing from the preview - values are synthesized from the
 * observed formatting instead ("32 GB" teaches "16 GB"); a term-query boost on a string no
 * product carries is a harmless no-op. Only full and equal-split configurations are boosted:
 * facets aggregate category-wide and Elastic cannot check per-product co-occurrence, so looser
 * decompositions (8+24) admit values that never appear together on one machine.
 */
const expandMemorySlotBoosts = (
    rawValue: string,
    facets: { [name: string]: string[] }
): IAttribute[] => {
    const total = parseCapacityGb(rawValue);
    if (total === undefined) return [];
    const slotKeys = Object.keys(facets).filter((k) => /(ram|memory)\s*slot/i.test(k));
    if (slotKeys.length === 0) return [];

    // Formatting templates learned from real slot values, GB units only ("16 GB" vs "16GB").
    const templates = new Set<string>();
    for (const key of slotKeys) {
        for (const v of facets[key] ?? []) {
            const m = v.trim().match(/^(\d+(?:\.\d+)?)(\s*)(gb)$/i);
            if (m) templates.add(`@${m[2]}${m[3]}`);
        }
    }
    if (templates.size === 0) {
        templates.add('@ GB');
        templates.add('@GB');
    }
    const fmt = (gb: number): string[] =>
        Number.isInteger(gb) && gb > 0 ? Array.from(templates).map((t) => t.replace('@', String(gb))) : [];

    // A half-split value is HALF the evidence: a lone 16 GB slot scores half a true 32 GB
    // total while a real 16+16 pair sums to tie it. Both ride the attribute-ask scale so the
    // user's ask outranks static merchandising boosts.
    const weighted = [
        ...fmt(total).map((value) => ({ value, weight: ATTRIBUTE_ASK_WEIGHT })),
        ...fmt(total / 2).map((value) => ({ value, weight: ATTRIBUTE_ASK_WEIGHT / 2 })),
    ];
    const out: IAttribute[] = [];
    for (const key of slotKeys) {
        for (const { value, weight } of weighted) out.push({ type: 'facet', name: key, value, weight });
    }
    return out;
};

/**
 * Expands attribute boosts against the real facet catalog: an exact attribute name normalizes its
 * value to the catalog's spelling; a pseudonym (ram, cpu, …) fans out into a boost on EVERY
 * matching attribute that carries a normalized-equal (or prefix) value. Unresolvable entries pass
 * through unchanged — a boost that matches nothing costs nothing.
 */
export const expandAttributeBoosts = (
    boosts: IAttribute[],
    facets: { [name: string]: string[] } | undefined
): IAttribute[] => {
    if (!facets || boosts.length === 0) return boosts;
    const keys = Object.keys(facets);
    // An exact normalized match is THE value (32gb → "32 GB"). A partial match means a family
    // ("i7" → every value containing it: "i7-1355U" AND "Core i7"), so every substring match is
    // boosted — prioritizing prefix matches dropped equally good mid-string ones.
    const valueMatches = (key: string, raw: string): string[] => {
        const norm = normalizeForMatch(raw);
        const values = facets[key] ?? [];
        const exact = values.filter((v) => normalizeForMatch(v) === norm);
        if (exact.length) return exact;
        return values.filter((v) => normalizeForMatch(v).includes(norm));
    };

    const out: IAttribute[] = [];
    for (const boost of boosts) {
        const groupStart = out.length;
        // A pseudonym name ("ram", "processor") always fans out across the alias's attributes,
        // even when a real attribute shares the name - the pseudonym means the domain, and its
        // values row shows a union, so the picked value may live on a sibling attribute.
        const isPseudonym = ATTRIBUTE_ALIASES[boost.name.toLowerCase()] !== undefined;
        const exact = isPseudonym ? undefined : keys.find((k) => k.toLowerCase() === boost.name.toLowerCase());
        const targets = ATTRIBUTE_ALIASES[boost.name.toLowerCase()] ?? [boost.name.toLowerCase()];
        let resolved = false;
        if (exact) {
            const matches = valueMatches(exact, boost.value);
            for (const value of matches.length ? matches : [boost.value]) {
                // An explicit attribute ask should outrank the query's static merchandising
                // boosts (favorites 2.0, has-image/has-cost 1.1), not tie with them.
                out.push({ type: 'facet', name: exact, value, weight: ATTRIBUTE_ASK_WEIGHT });
            }
            resolved = true;
        } else {
            const candidates = keys
                .filter((k) => aliasMatchesKey(boost.name, k, facets[k]))
                .sort((a, b) => (facets[b]?.length ?? 0) - (facets[a]?.length ?? 0));
            for (const key of candidates) {
                for (const value of valueMatches(key, boost.value)) {
                    out.push({ type: 'facet', name: key, value, weight: ATTRIBUTE_ASK_WEIGHT });
                    resolved = true;
                }
            }
        }
        // Memory asks reach per-slot attributes whichever path resolved the name — the value
        // pill picker commits the REAL attribute name ("RAM"), which must not skip slot
        // inference. Explicitly picked slot attributes stay as picked.
        const resolvedName = exact ?? boost.name;
        const memoryIsh = (targets.includes('memory') || /\b(ram|memory)\b/i.test(resolvedName))
            && !CAPACITY_EXCLUDES.some((x) => resolvedName.toLowerCase().includes(x));
        const isSlotItself = /(ram|memory)\s*slot/i.test(resolvedName);
        if (memoryIsh && !isSlotItself) {
            for (const slotBoost of expandMemorySlotBoosts(boost.value, facets)) {
                out.push(slotBoost);
                resolved = true;
            }
        }
        if (!resolved) out.push(boost);
        // A fan-out is one ask - label every derived boost so the search page can render them
        // as a single dismiss-together pill instead of a wall of pills.
        if (out.length - groupStart > 1) {
            const group = `${boost.name} = ${boost.value}`;
            for (let i = groupStart; i < out.length; i++) out[i] = { ...out[i], group };
        }
    }
    // The slot expansion can revisit a candidate the plain match already added.
    const seen = new Set<string>();
    return out.filter((b) => {
        const id = `${b.name.toLowerCase()}||${b.value.toLowerCase()}`;
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
    });
};
